// biome-ignore-all lint/suspicious/noImplicitAnyLet: TODO - address later
import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  defaultDropAnimationSideEffects,
  MeasuringStrategy,
  MouseSensor,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { Filter } from "lucide-react";
import React, { useCallback, useMemo, useRef } from "react";
import {
  ActiveFiltersDisplay,
  type FilterColumn,
} from "@/components/active-filters-display";
import { EntityFilterModal } from "@/components/entity-filter-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useWipReportCardMetricsEnabled } from "@/hooks/use-feature-flags";
import { useTrackedSessionStorage } from "@/hooks/use-tracked-browser-storage";
import type { FilterClearingInfo } from "@/lib/filter-display-utils";
import { BenchmarkMetricCard } from "./benchmark-metric-card";
import { DraggableCard } from "./draggable-card";
import { MetricCard } from "./metric-card";
import ResetCardMenu from "./reset-card-menu";
import type { MetricCardConfig } from "./types";

interface MetricCardsContainerProps<T> {
  metrics: T | undefined;
  onMetricSelect: (metric: string) => void;
  loadingStates: Record<string, { isLoading: boolean; isError: boolean }>;
  metricConfig: Record<string, MetricCardConfig>;
  defaultOrder: string[];
  hiddenMetrics?: string[];
  wipMetrics?: string[];
  storageKey: string;
  title: string;
  entityScope: string;
  entity: "campaign" | "channel" | "salesperson" | "webpage";
  renderMetric: (
    value: number | null,
    options: Record<string, unknown>,
  ) => React.ReactNode;
  benchmarkMetrics?: T;
  benchmarkLoadingStates?: Record<
    string,
    { isLoading: boolean; isError: boolean }
  >;
  showBenchmark?: boolean;
  getTotalFilterCount?: () => number;
  onResetMetricFilters?: () => void;
  displayFilters?: Record<string, unknown>;
  filterColumns?: FilterColumn[];
  clearFilter?: (key: string, clearingInfo?: FilterClearingInfo) => void;
  leftControls?: React.ReactNode;
  showBody?: boolean;
  emptyBody?: React.ReactNode;
  hideCampaignFilters?: boolean;
}

export function MetricCardsContainer<T>({
  metrics,
  onMetricSelect,
  loadingStates,
  metricConfig,
  defaultOrder,
  hiddenMetrics = [],
  wipMetrics = [],
  storageKey,
  title,
  entityScope,
  entity,
  renderMetric,
  benchmarkMetrics,
  benchmarkLoadingStates,
  showBenchmark = false,
  getTotalFilterCount,
  onResetMetricFilters,
  displayFilters,
  filterColumns,
  clearFilter,
  leftControls,
  showBody = true,
  emptyBody,
  hideCampaignFilters = false,
}: MetricCardsContainerProps<T>) {
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [showFilterModal, setShowFilterModal] = React.useState(false);
  const wipMetricsEnabled = useWipReportCardMetricsEnabled();
  const gridRef = useRef<HTMLDivElement>(null);

  // Get filter count from provided function or default to 0
  const totalFilterCount = useMemo(() => {
    return getTotalFilterCount ? getTotalFilterCount() : 0;
  }, [getTotalFilterCount]);

  // Persist order and emit tracked events. Support sessionStorage (with migration from localStorage)
  const [sessionOrder, setSessionOrder] = useTrackedSessionStorage<string[]>(
    storageKey,
    defaultOrder,
    {
      migration: {
        from: {
          key: storageKey,
          storage: "localStorage",
          organizationScoped: true,
        },
        cleanup: true,
      },
    },
  );
  const storedOrder = sessionOrder;
  const setStoredOrder = setSessionOrder;

  // Function to reset the order to default
  const resetToDefaultOrder = useCallback(() => {
    setStoredOrder(defaultOrder);
  }, [setStoredOrder, defaultOrder]);

  // Merge any new metrics into the stored order
  const metricOrder = useMemo(() => {
    const allMetrics = Object.keys(metricConfig) as string[];

    // Filter out WIP metrics if feature flag is disabled
    const availableMetrics = wipMetricsEnabled
      ? allMetrics
      : allMetrics.filter((metric) => !wipMetrics.includes(metric));

    // Filter out hidden metrics
    const visibleMetrics = availableMetrics.filter(
      (metric) => !hiddenMetrics.includes(metric),
    );

    // Filter out any stored metrics that no longer exist and add new ones
    const validStoredMetrics = storedOrder.filter((metric) =>
      visibleMetrics.includes(metric),
    );
    const newMetrics = visibleMetrics.filter(
      (metric) => !validStoredMetrics.includes(metric),
    );
    return [...validStoredMetrics, ...newMetrics];
  }, [storedOrder, wipMetricsEnabled, metricConfig, hiddenMetrics, wipMetrics]);

  // Update stored order if it contains invalid metrics
  React.useEffect(() => {
    const allMetrics = Object.keys(metricConfig) as string[];
    const availableMetrics = wipMetricsEnabled
      ? allMetrics
      : allMetrics.filter((metric) => !wipMetrics.includes(metric));

    const hasInvalidMetrics = storedOrder.some(
      (metric) => !availableMetrics.includes(metric),
    );
    if (hasInvalidMetrics) {
      setStoredOrder(metricOrder);
    }
  }, [
    storedOrder,
    metricOrder,
    setStoredOrder,
    wipMetricsEnabled,
    metricConfig,
    wipMetrics,
  ]);

  // Setup draggable sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor),
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = metricOrder.indexOf(active.id as string);
      const newIndex = metricOrder.indexOf(over?.id as string);

      // Create a new order that shifts items in between the old and new positions
      const newOrder = [...metricOrder];
      const movedItem = newOrder[oldIndex];

      // Remove the item from its old position
      newOrder.splice(oldIndex, 1);

      // Insert the item at its new position, shifting everything else
      newOrder.splice(newIndex, 0, movedItem);

      // Update the state with the new order
      setStoredOrder(newOrder);
    }

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  // Filter out metrics that are not available (error state)
  const availableMetrics = useMemo(() => {
    return metricOrder.filter(
      (metricKey) => !loadingStates[metricKey]?.isError,
    );
  }, [metricOrder, loadingStates]);

  // Helper function to create render options for metric rendering
  function getRenderOptions(metricKey: string) {
    return {
      metricKey,
      metrics,
      benchmarkMetrics,
      isBenchmark: false,
    };
  }

  // Helper function to render a metric card
  const renderMetricCard = (metricKey: string) => {
    const info = metricConfig[metricKey];
    if (!info) {
      console.warn(`Metric config not found for key: ${metricKey}`);
      return null;
    }

    const metricValue = metrics?.[metricKey as keyof T];
    const rawValue = typeof metricValue === "number" ? metricValue : null;
    const isMetricLoading = loadingStates[metricKey]?.isLoading;
    const isWipMetric = wipMetrics.includes(metricKey);

    // Define options for the render function
    const renderOptions = getRenderOptions(metricKey);

    // Calculate formatted value using render logic
    const formattedValue = info.render
      ? info.render(rawValue, renderOptions)
      : renderMetric(rawValue, renderOptions);

    // Benchmark related data
    let benchmarkFormattedValue;
    let isBenchmarkLoading = false;

    if (showBenchmark && benchmarkMetrics) {
      const benchmarkMetricValue = benchmarkMetrics[metricKey as keyof T];
      const benchmarkRawValue =
        typeof benchmarkMetricValue === "number" ? benchmarkMetricValue : null;
      isBenchmarkLoading =
        benchmarkLoadingStates?.[metricKey]?.isLoading || false;

      // Calculate benchmark formatted value with isBenchmark=true
      const benchmarkRenderOptions = {
        ...renderOptions,
        isBenchmark: true,
      };

      benchmarkFormattedValue = info.render
        ? info.render(benchmarkRawValue, benchmarkRenderOptions)
        : renderMetric(benchmarkRawValue, benchmarkRenderOptions);
    }

    return (
      <div key={metricKey}>
        <DraggableCard<T>
          id={metricKey}
          title={info.title}
          value={formattedValue}
          isLoading={isMetricLoading}
          onDrilldown={() => onMetricSelect(metricKey)}
          isWip={isWipMetric}
          tooltip={info.tooltip}
          category={info.category}
          benchmarkValue={benchmarkFormattedValue}
          benchmarkLoading={isBenchmarkLoading}
          showBenchmark={showBenchmark}
          metrics={metrics}
        />
      </div>
    );
  };

  return (
    <Card className="border border-border/40 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <CardTitle className="text-lg font-medium">{title}</CardTitle>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {leftControls && (
              <div className="flex flex-wrap items-center gap-2">
                {leftControls}
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-1.5"
            >
              <Filter
                className={`h-4 w-4 ${
                  totalFilterCount > 0
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              />
              <span>Metric Filters</span>
              {totalFilterCount > 0 && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {totalFilterCount}
                </Badge>
              )}
            </Button>
            <ResetCardMenu
              onResetOrder={resetToDefaultOrder}
              onResetMetricFilters={onResetMetricFilters}
              onResetAll={() => {
                resetToDefaultOrder();
                onResetMetricFilters?.();
              }}
            />
          </div>
        </div>

        {displayFilters &&
          filterColumns &&
          clearFilter &&
          onResetMetricFilters &&
          Object.keys(displayFilters).length > 0 && (
            <div className="pt-4">
              <ActiveFiltersDisplay
                filters={displayFilters}
                columns={filterColumns}
                clearFilter={clearFilter}
                clearAllFilters={onResetMetricFilters}
                clearAllText="Clear all metric filters"
                filterType="metric"
              />
            </div>
          )}
      </CardHeader>

      <div className="border-b border-border/40"></div>

      <CardContent className="p-4 space-y-4">
        {!showBody ? (
          emptyBody || (
            <div className="py-8 text-center text-muted-foreground">
              Nothing to display
            </div>
          )
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            measuring={{
              droppable: {
                strategy: MeasuringStrategy.Always,
              },
            }}
          >
            <SortableContext
              items={availableMetrics}
              strategy={rectSortingStrategy}
            >
              <div className="overflow-hidden">
                <div className="relative">
                  <div
                    ref={gridRef}
                    className="grid gap-4 md:gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 relative transition-all duration-200 ease-out"
                  >
                    {availableMetrics.map(renderMetricCard)}
                  </div>
                </div>
              </div>
            </SortableContext>
            <DragOverlay
              dropAnimation={{
                duration: 150,
                easing: "ease",
                sideEffects: defaultDropAnimationSideEffects({
                  styles: {
                    active: {
                      opacity: "0.8",
                    },
                  },
                }),
              }}
            >
              {activeId &&
                (() => {
                  const info = metricConfig[activeId];
                  if (!info) {
                    return null;
                  }

                  const metricValue = metrics?.[activeId as keyof T];
                  const rawValue =
                    typeof metricValue === "number" ? metricValue : null;
                  const isMetricLoading = loadingStates[activeId]?.isLoading;
                  const isWipMetric = wipMetrics.includes(activeId);

                  // Define options for the render function
                  const renderOptions = getRenderOptions(activeId);

                  // Calculate formatted value using render logic
                  const formattedValue = info.render
                    ? info.render(rawValue, renderOptions)
                    : renderMetric(rawValue, renderOptions);

                  // Benchmark related data for overlay
                  let benchmarkFormattedValue;
                  let isBenchmarkLoading = false;

                  if (showBenchmark && benchmarkMetrics) {
                    const benchmarkMetricValue =
                      benchmarkMetrics[activeId as keyof T];
                    const benchmarkRawValue =
                      typeof benchmarkMetricValue === "number"
                        ? benchmarkMetricValue
                        : null;
                    isBenchmarkLoading =
                      benchmarkLoadingStates?.[activeId]?.isLoading || false;

                    const benchmarkRenderOptions = {
                      ...renderOptions,
                      isBenchmark: true,
                    };

                    benchmarkFormattedValue = info.render
                      ? info.render(benchmarkRawValue, benchmarkRenderOptions)
                      : renderMetric(benchmarkRawValue, benchmarkRenderOptions);
                  }

                  const overlayProps = {
                    id: activeId,
                    title: info.title,
                    value: formattedValue,
                    isLoading: isMetricLoading,
                    isWip: isWipMetric,
                    tooltip: info.tooltip,
                    category: info.category,
                    isDraggingOverlay: true,
                    benchmarkValue: benchmarkFormattedValue,
                    benchmarkLoading: isBenchmarkLoading,
                    metrics: metrics,
                  };

                  return (
                    <div className="shadow-md rounded-lg">
                      {showBenchmark ? (
                        <BenchmarkMetricCard<T> {...overlayProps} />
                      ) : (
                        <MetricCard<T> {...overlayProps} />
                      )}
                    </div>
                  );
                })()}
            </DragOverlay>
          </DndContext>
        )}
      </CardContent>
      <EntityFilterModal
        open={showFilterModal}
        onOpenChange={setShowFilterModal}
        scope={entityScope}
        entity={entity}
        hideCampaignFilters={hideCampaignFilters}
      />
    </Card>
  );
}
